import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Id } from '../../../../convex/_generated/dataModel'
import { Mail, Send, Loader2, CheckCircle2, AlertCircle, Search, ChevronDown, X, MessageSquare, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/contexts/auth-context'

type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed'
type StatusFilter = 'all' | TicketStatus

const STATUS_CONFIG: Record<TicketStatus, { label: string; color: string }> = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700' },
  closed: { label: 'Closed', color: 'bg-neutral-100 text-neutral-500' },
}

function getEffectiveStatus(status: string): TicketStatus {
  if (status === 'unread' || status === 'read') return 'open'
  if (status === 'archived') return 'closed'
  return status as TicketStatus
}

function formatTimeShort(timestamp: number): string {
  const now = Date.now()
  const diffMs = now - timestamp
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ─── Top Toolbar ────────────────────────────────────────────────────────────

type Channel = 'email' | 'chat'

function ChannelSwitch({ channel, setChannel }: { channel: Channel; setChannel: (c: Channel) => void }) {
  const tabs: { label: string; value: Channel; icon: typeof Mail }[] = [
    { label: 'Email', value: 'email', icon: Mail },
    { label: 'In-app chat', value: 'chat', icon: MessageSquare },
  ]
  return (
    <div className="flex items-center gap-0.5 p-0.5 bg-neutral-100 rounded-lg shrink-0">
      {tabs.map((t) => {
        const Icon = t.icon
        return (
          <button
            key={t.value}
            onClick={() => setChannel(t.value)}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
              channel === t.value
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-500 hover:text-neutral-700',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

function TopToolbar({
  channel,
  setChannel,
  openCount,
  statusFilter,
  setStatusFilter,
  searchQuery,
  setSearchQuery,
  showSearch = true,
}: {
  channel: Channel
  setChannel: (c: Channel) => void
  openCount: number
  statusFilter: StatusFilter
  setStatusFilter: (f: StatusFilter) => void
  searchQuery: string
  setSearchQuery: (q: string) => void
  showSearch?: boolean
}) {
  const filters: { label: string; value: StatusFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'Open', value: 'open' },
    { label: 'In Prog', value: 'in_progress' },
    { label: 'Resolved', value: 'resolved' },
    { label: 'Closed', value: 'closed' },
  ]

  return (
    <div className="px-4 py-3 border-b border-neutral-200 flex items-center gap-3 shrink-0 bg-white">
      <h1 className="text-base font-semibold text-neutral-900 shrink-0">Support</h1>
      <ChannelSwitch channel={channel} setChannel={setChannel} />
      {openCount > 0 && (
        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full shrink-0">
          {openCount} open
        </span>
      )}
      <div className="flex items-center gap-0.5 ml-2">
        {filters.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={cn(
              'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
              statusFilter === f.value
                ? 'bg-neutral-900 text-white'
                : 'text-neutral-500 hover:bg-neutral-100'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>
      {showSearch && (
        <div className="ml-auto relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-xs rounded-md border border-neutral-200 bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
          />
        </div>
      )}
    </div>
  )
}

// ─── Ticket List (Left Panel) ───────────────────────────────────────────────

function TicketList({
  emails,
  selectedId,
  selectedIds,
  onSelect,
  onToggleCheck,
  onSelectAll,
}: {
  emails: any[]
  selectedId: Id<'supportEmails'> | null
  selectedIds: Set<string>
  onSelect: (id: Id<'supportEmails'>) => void
  onToggleCheck: (id: string) => void
  onSelectAll: () => void
}) {
  const allChecked = emails.length > 0 && selectedIds.size === emails.length
  const someChecked = selectedIds.size > 0 && selectedIds.size < emails.length
  const checkAllRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (checkAllRef.current) {
      checkAllRef.current.indeterminate = someChecked
    }
  }, [someChecked])

  return (
    <div className="flex flex-col h-full">
      {/* Select-all header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
        <input
          ref={checkAllRef}
          type="checkbox"
          checked={allChecked}
          onChange={onSelectAll}
          className="h-3.5 w-3.5 rounded border-neutral-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        />
        <span className="text-[11px] text-neutral-400">
          {selectedIds.size > 0 ? `${selectedIds.size} selected` : `${emails.length} tickets`}
        </span>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto">
        {emails.length === 0 ? (
          <div className="p-8 text-center">
            <Mail className="h-6 w-6 text-neutral-300 mx-auto mb-2" />
            <p className="text-xs text-neutral-400">No tickets found</p>
          </div>
        ) : (
          emails.map((email) => {
            const status = getEffectiveStatus(email.status)
            const config = STATUS_CONFIG[status]
            const isSelected = email._id === selectedId
            const isChecked = selectedIds.has(email._id)
            const displayName = email.from.replace(/<[^>]+>/, '').trim() || email.fromEmail

            return (
              <div
                key={email._id}
                className={cn(
                  'flex items-start gap-2 px-3 py-3 border-b border-neutral-100 cursor-pointer transition-colors group',
                  isSelected ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-neutral-50 border-l-2 border-l-transparent',
                )}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    e.stopPropagation()
                    onToggleCheck(email._id)
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="h-3.5 w-3.5 rounded border-neutral-300 text-blue-600 focus:ring-blue-500 mt-1 shrink-0 cursor-pointer"
                />
                <div
                  className="flex-1 min-w-0"
                  onClick={() => onSelect(email._id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {status === 'open' && (
                        <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                      )}
                      <span className="text-sm font-medium text-neutral-900 truncate">
                        {displayName}
                      </span>
                    </div>
                    <span className="text-[11px] text-neutral-400 shrink-0">
                      {formatTimeShort(email.receivedAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={cn('text-[10px] font-medium px-1.5 py-0 rounded', config.color)}>
                      {config.label}
                    </span>
                    <span className="text-xs font-medium text-neutral-700 truncate">
                      {email.subject}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-[11px] text-neutral-400 truncate">
                      {email.text?.slice(0, 80) || '(No content)'}
                    </p>
                    <span className="text-[10px] text-neutral-300 font-mono shrink-0 ml-2">
                      #{String(email.ticketNumber ?? 0).padStart(3, '0')}
                    </span>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ─── Batch Action Bar ───────────────────────────────────────────────────────

function BatchActionBar({
  count,
  onSetStatus,
  onClear,
}: {
  count: number
  onSetStatus: (status: TicketStatus) => void
  onClear: () => void
}) {
  return (
    <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-neutral-900 text-white flex items-center gap-3 z-20">
      <span className="text-xs font-medium">{count} selected</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-white/10 hover:bg-white/20 rounded-md transition-colors">
            Set Status
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => onSetStatus('open')}>Open</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSetStatus('in_progress')}>In Progress</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSetStatus('resolved')}>Resolved</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSetStatus('closed')}>Closed</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        onClick={onClear}
        className="ml-auto p-1 hover:bg-white/10 rounded transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ─── Ticket Detail (Right Panel) ────────────────────────────────────────────

function TicketDetail({ email }: { email: any }) {
  const [replyText, setReplyText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const threadRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()

  const setStatus = useMutation(api.supportEmails.setStatus)
  const sendReplyAction = useAction(api.supportEmails.sendReply)
  const replies = useQuery(api.supportEmails.listReplies, { ticketId: email._id })

  const effectiveStatus = getEffectiveStatus(email.status)

  // Scroll thread to top on ticket switch
  useEffect(() => {
    threadRef.current?.scrollTo(0, 0)
  }, [email._id])

  const handleStatusChange = async (newStatus: TicketStatus) => {
    await setStatus({ id: email._id, status: newStatus })
  }

  const handleSendReply = async () => {
    if (!replyText.trim() || isSending) return
    setIsSending(true)
    try {
      const result = await sendReplyAction({
        ticketId: email._id,
        message: replyText.trim(),
        senderName: user?.name || user?.email || 'Admin',
        senderUserId: user?._id as Id<'users'> | undefined,
      })
      if (result.success) {
        setReplyText('')
      }
    } catch {
      console.error('Failed to send reply')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-5 py-3 border-b border-neutral-200 shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-neutral-400">
            #{String(email.ticketNumber ?? 0).padStart(3, '0')}
          </span>
          <h2 className="text-sm font-semibold text-neutral-900 truncate flex-1">
            {email.subject}
          </h2>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn(
                'flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors',
                STATUS_CONFIG[effectiveStatus].color,
              )}>
                {STATUS_CONFIG[effectiveStatus].label}
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleStatusChange('open')}>Open</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('in_progress')}>In Progress</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('resolved')}>Resolved</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('closed')}>Closed</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-neutral-500">
          <span>{email.fromEmail}</span>
          <span>&middot;</span>
          <span>{formatTimeShort(email.receivedAt)}</span>
        </div>
      </div>

      {/* Conversation thread */}
      <div ref={threadRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {/* Original email */}
        <div className="rounded-lg border border-neutral-200 p-4">
          <div className="flex items-center gap-2 mb-2 text-xs text-neutral-500">
            <span className="font-medium text-neutral-700">{email.fromEmail}</span>
            <span>&middot;</span>
            <span>{new Date(email.receivedAt).toLocaleString()}</span>
          </div>
          {email.html ? (
            <div
              className="prose prose-sm max-w-none text-neutral-700"
              dangerouslySetInnerHTML={{ __html: email.html }}
            />
          ) : (
            <pre className="text-sm text-neutral-700 whitespace-pre-wrap font-sans leading-relaxed">
              {email.text || '(No content)'}
            </pre>
          )}
        </div>

        {/* Threaded replies */}
        {replies?.map((reply) =>
          reply.direction === 'inbound' ? (
            <div key={reply._id} className="rounded-lg border border-neutral-200 p-4">
              <div className="flex items-center gap-2 mb-2 text-xs text-neutral-500">
                <span className="font-medium text-neutral-700">{reply.sentBy}</span>
                <span>&middot;</span>
                <span>{new Date(reply.sentAt).toLocaleString()}</span>
              </div>
              {reply.html ? (
                <div
                  className="prose prose-sm max-w-none text-neutral-700"
                  dangerouslySetInnerHTML={{ __html: reply.html }}
                />
              ) : (
                <pre className="text-sm text-neutral-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {reply.message}
                </pre>
              )}
            </div>
          ) : (
            <div key={reply._id} className="flex justify-end">
              <div className="max-w-[80%] rounded-lg bg-blue-50 border border-blue-100 p-4">
                <div className="flex items-center gap-2 mb-2 text-xs text-neutral-500">
                  <span className="font-medium text-blue-700">{reply.sentBy}</span>
                  <span>&middot;</span>
                  <span>{new Date(reply.sentAt).toLocaleString()}</span>
                  <span className="ml-auto">
                    {reply.deliveryStatus === 'sending' && (
                      <Loader2 className="h-3 w-3 animate-spin text-neutral-400" />
                    )}
                    {reply.deliveryStatus === 'sent' && (
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                    )}
                    {reply.deliveryStatus === 'failed' && (
                      <span className="flex items-center gap-1 text-red-500">
                        <AlertCircle className="h-3 w-3" />
                        <span className="text-[10px]">Failed</span>
                      </span>
                    )}
                  </span>
                </div>
                <pre className="text-sm text-neutral-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {reply.message}
                </pre>
              </div>
            </div>
          )
        )}
      </div>

      {/* Reply composer */}
      <div className="border-t border-neutral-200 px-5 py-3 shrink-0">
        <div className="relative">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply..."
            rows={3}
            className="w-full resize-none rounded-lg border border-neutral-200 px-3 py-2 pr-20 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) {
                handleSendReply()
              }
            }}
          />
          <div className="absolute bottom-2 right-2 flex items-center gap-2">
            <span className="text-[10px] text-neutral-300">⌘+Enter</span>
            <button
              onClick={handleSendReply}
              disabled={!replyText.trim() || isSending}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors',
                replyText.trim() && !isSending
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
              )}
            >
              {isSending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Send className="h-3 w-3" />
              )}
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Empty Selection Placeholder ────────────────────────────────────────────

function EmptySelection({
  icon: Icon = Mail,
  label = 'Select a ticket to view',
}: {
  icon?: typeof Mail
  label?: string
}) {
  return (
    <div className="h-full flex items-center justify-center bg-neutral-50">
      <div className="text-center">
        <Icon className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
        <p className="text-sm text-neutral-500">{label}</p>
      </div>
    </div>
  )
}

// ─── Email Inbox ──────────────────────────────────────────────────────────────

function EmailInbox({ channel, setChannel }: { channel: Channel; setChannel: (c: Channel) => void }) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [selectedId, setSelectedId] = useState<Id<'supportEmails'> | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  const emails = useQuery(api.supportEmails.list, {})
  const batchSetStatus = useMutation(api.supportEmails.batchSetStatus)

  // Filter emails by status and search
  const filteredEmails = useMemo(() => {
    if (!emails) return []
    return emails.filter((e) => {
      const status = getEffectiveStatus(e.status)
      if (statusFilter !== 'all' && status !== statusFilter) return false
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const matchSubject = e.subject.toLowerCase().includes(q)
        const matchEmail = e.fromEmail.toLowerCase().includes(q)
        const matchName = e.from.toLowerCase().includes(q)
        if (!matchSubject && !matchEmail && !matchName) return false
      }
      return true
    })
  }, [emails, statusFilter, searchQuery])

  const openCount = useMemo(
    () => emails?.filter((e) => getEffectiveStatus(e.status) === 'open').length ?? 0,
    [emails],
  )

  const selectedEmail = emails?.find((e) => e._id === selectedId)

  // Clear batch selection when filter/search changes
  const handleSetStatusFilter = useCallback((f: StatusFilter) => {
    setStatusFilter(f)
    setSelectedIds(new Set())
  }, [])

  const handleSetSearchQuery = useCallback((q: string) => {
    setSearchQuery(q)
    setSelectedIds(new Set())
  }, [])

  const handleToggleCheck = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === filteredEmails.length) return new Set()
      return new Set(filteredEmails.map((e) => e._id))
    })
  }, [filteredEmails])

  const handleBatchSetStatus = useCallback(
    async (status: TicketStatus) => {
      const ids = Array.from(selectedIds) as Id<'supportEmails'>[]
      if (ids.length === 0) return
      await batchSetStatus({ ids, status })
      setSelectedIds(new Set())
    },
    [selectedIds, batchSetStatus],
  )

  return (
    <div className="h-full flex flex-col bg-white">
      <TopToolbar
        channel={channel}
        setChannel={setChannel}
        openCount={openCount}
        statusFilter={statusFilter}
        setStatusFilter={handleSetStatusFilter}
        searchQuery={searchQuery}
        setSearchQuery={handleSetSearchQuery}
      />
      <div className="flex-1 flex min-h-0">
        {/* Left panel */}
        <div className="w-[420px] border-r border-neutral-200 flex flex-col relative shrink-0">
          <TicketList
            emails={filteredEmails}
            selectedId={selectedId}
            selectedIds={selectedIds}
            onSelect={setSelectedId}
            onToggleCheck={handleToggleCheck}
            onSelectAll={handleSelectAll}
          />
          {selectedIds.size > 0 && (
            <BatchActionBar
              count={selectedIds.size}
              onSetStatus={handleBatchSetStatus}
              onClear={() => setSelectedIds(new Set())}
            />
          )}
          {/* Footer */}
          <div className="px-3 py-1.5 border-t border-neutral-100 shrink-0 text-[11px] text-neutral-400">
            {filteredEmails.length} ticket{filteredEmails.length !== 1 ? 's' : ''}
          </div>
        </div>
        {/* Right panel */}
        <div className="flex-1 min-w-0">
          {selectedEmail ? (
            <TicketDetail email={selectedEmail} />
          ) : (
            <EmptySelection />
          )}
        </div>
      </div>
    </div>
  )
}

// ─── In-app Chat: Role bubble ─────────────────────────────────────────────────

const ROLE_CONFIG: Record<string, { label: string; align: 'left' | 'right'; bubble: string; name: string }> = {
  user: { label: 'User', align: 'left', bubble: 'bg-neutral-100 border-neutral-200', name: 'text-neutral-700' },
  ai: { label: 'AI', align: 'left', bubble: 'bg-violet-50 border-violet-100', name: 'text-violet-700' },
  founder: { label: 'Founder', align: 'right', bubble: 'bg-blue-50 border-blue-100', name: 'text-blue-700' },
}

function NeedsHumanBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-700">
      <AlertTriangle className="h-3 w-3" />
      Needs human
    </span>
  )
}

// ─── In-app Chat: Thread List (Left Panel) ────────────────────────────────────

function ThreadList({
  threads,
  selectedId,
  onSelect,
}: {
  threads: any[]
  selectedId: Id<'supportThreads'> | null
  onSelect: (id: Id<'supportThreads'>) => void
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
        <span className="text-[11px] text-neutral-400">{threads.length} threads</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare className="h-6 w-6 text-neutral-300 mx-auto mb-2" />
            <p className="text-xs text-neutral-400">No chat threads found</p>
          </div>
        ) : (
          threads.map((t) => {
            const status = getEffectiveStatus(t.status)
            const config = STATUS_CONFIG[status]
            const isSelected = t._id === selectedId
            return (
              <div
                key={t._id}
                onClick={() => onSelect(t._id)}
                className={cn(
                  'px-3 py-3 border-b border-neutral-100 cursor-pointer transition-colors',
                  isSelected
                    ? 'bg-blue-50 border-l-2 border-l-blue-500'
                    : 'hover:bg-neutral-50 border-l-2 border-l-transparent',
                  t.needsHuman && !isSelected && 'bg-red-50/40',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {status === 'open' && <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />}
                    <span className="text-sm font-medium text-neutral-900 truncate">{t.userEmail}</span>
                  </div>
                  <span className="text-[11px] text-neutral-400 shrink-0">{formatTimeShort(t.lastMessageAt)}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className={cn('text-[10px] font-medium px-1.5 py-0 rounded', config.color)}>
                    {config.label}
                  </span>
                  {t.needsHuman && <NeedsHumanBadge />}
                  {t.subject && (
                    <span className="text-xs font-medium text-neutral-700 truncate">{t.subject}</span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <p className="text-[11px] text-neutral-400 truncate">
                    {t.lastMessageRole === 'founder' ? 'You: ' : t.lastMessageRole === 'ai' ? 'AI: ' : ''}
                    {t.lastMessagePreview || '(No messages)'}
                  </p>
                  <span className="text-[10px] text-neutral-300 font-mono shrink-0 ml-2">
                    {t.messageCount} msg
                  </span>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ─── In-app Chat: Thread Detail (Right Panel) ─────────────────────────────────

function ThreadDetail({ threadId }: { threadId: Id<'supportThreads'> }) {
  const [replyText, setReplyText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const threadRef = useRef<HTMLDivElement>(null)

  const detail = useQuery(api.supportAdmin.threadDetail, { threadId })
  const founderReply = useMutation(api.supportAdmin.founderReply)
  const setThreadStatus = useMutation(api.supportAdmin.setThreadStatus)

  // Scroll to bottom when messages change / thread switches
  useEffect(() => {
    threadRef.current?.scrollTo(0, threadRef.current.scrollHeight)
  }, [threadId, detail?.messages.length])

  if (detail === undefined) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-300" />
      </div>
    )
  }
  if (detail === null) return <EmptySelection icon={MessageSquare} label="Thread not found" />

  const { thread, messages, userEmail, userName } = detail
  const effectiveStatus = getEffectiveStatus(thread.status)

  const handleStatusChange = async (newStatus: TicketStatus) => {
    await setThreadStatus({ threadId, status: newStatus })
  }

  const handleSendReply = async () => {
    if (!replyText.trim() || isSending) return
    setIsSending(true)
    try {
      await founderReply({ threadId, content: replyText.trim() })
      setReplyText('')
    } catch {
      console.error('Failed to send founder reply')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-5 py-3 border-b border-neutral-200 shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-neutral-900 truncate flex-1">
            {thread.subject || userName || userEmail}
          </h2>
          {thread.needsHuman && <NeedsHumanBadge />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn(
                'flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors',
                STATUS_CONFIG[effectiveStatus].color,
              )}>
                {STATUS_CONFIG[effectiveStatus].label}
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleStatusChange('open')}>Open</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('in_progress')}>In Progress</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('resolved')}>Resolved</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('closed')}>Closed</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2 mt-1 text-xs text-neutral-500">
          <span>{userEmail}</span>
          <span>&middot;</span>
          <span>{formatTimeShort(thread.lastMessageAt)}</span>
        </div>
      </div>

      {/* Conversation thread */}
      <div ref={threadRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {messages.length === 0 ? (
          <p className="text-xs text-neutral-400 text-center py-8">No messages yet</p>
        ) : (
          messages.map((m) => {
            const config = ROLE_CONFIG[m.role] ?? ROLE_CONFIG.user
            return (
              <div key={m._id} className={cn('flex', config.align === 'right' ? 'justify-end' : 'justify-start')}>
                <div className={cn('max-w-[80%] rounded-lg border p-4', config.bubble)}>
                  <div className="flex items-center gap-2 mb-2 text-xs text-neutral-500">
                    <span className={cn('font-medium', config.name)}>
                      {m.authorName || config.label}
                    </span>
                    <span>&middot;</span>
                    <span>{new Date(m.createdAt).toLocaleString()}</span>
                  </div>
                  <pre className="text-sm text-neutral-700 whitespace-pre-wrap font-sans leading-relaxed">
                    {m.content}
                  </pre>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Founder reply composer */}
      <div className="border-t border-neutral-200 px-5 py-3 shrink-0">
        <div className="relative">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Reply as the team (also posts to Slack)..."
            rows={3}
            className="w-full resize-none rounded-lg border border-neutral-200 px-3 py-2 pr-20 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) {
                handleSendReply()
              }
            }}
          />
          <div className="absolute bottom-2 right-2 flex items-center gap-2">
            <span className="text-[10px] text-neutral-300">⌘+Enter</span>
            <button
              onClick={handleSendReply}
              disabled={!replyText.trim() || isSending}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors',
                replyText.trim() && !isSending
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
              )}
            >
              {isSending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── In-app Chat Inbox ────────────────────────────────────────────────────────

function ChatInbox({ channel, setChannel }: { channel: Channel; setChannel: (c: Channel) => void }) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [selectedId, setSelectedId] = useState<Id<'supportThreads'> | null>(null)

  const threads = useQuery(
    api.supportAdmin.listThreads,
    statusFilter === 'all' ? {} : { status: statusFilter },
  )

  const openCount = useMemo(
    () => threads?.filter((t) => getEffectiveStatus(t.status) === 'open').length ?? 0,
    [threads],
  )

  const list = threads ?? []

  return (
    <div className="h-full flex flex-col bg-white">
      <TopToolbar
        channel={channel}
        setChannel={setChannel}
        openCount={openCount}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        searchQuery=""
        setSearchQuery={() => {}}
        showSearch={false}
      />
      <div className="flex-1 flex min-h-0">
        {/* Left panel */}
        <div className="w-[420px] border-r border-neutral-200 flex flex-col relative shrink-0">
          <ThreadList threads={list} selectedId={selectedId} onSelect={setSelectedId} />
          <div className="px-3 py-1.5 border-t border-neutral-100 shrink-0 text-[11px] text-neutral-400">
            {list.length} thread{list.length !== 1 ? 's' : ''}
          </div>
        </div>
        {/* Right panel */}
        <div className="flex-1 min-w-0">
          {selectedId ? (
            <ThreadDetail threadId={selectedId} />
          ) : (
            <EmptySelection icon={MessageSquare} label="Select a thread to view" />
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function AdminSupportPage() {
  const [channel, setChannel] = useState<Channel>('email')
  return channel === 'email' ? (
    <EmailInbox channel={channel} setChannel={setChannel} />
  ) : (
    <ChatInbox channel={channel} setChannel={setChannel} />
  )
}
